"use client";

/**
 * v9.253 — AnatomySvg: detailed anatomical heatmap.
 *
 * Loads one of two anatomy SVGs (front / back) from /public/anatomy
 * and paints each muscle <g> based on the `fillFor` callback the
 * caller supplies. Replaces the earlier hand-coded geometric paths
 * in BodySilhouette for a much more anatomically convincing render
 * — every body region we track in the BodyRegion taxonomy has its
 * own dedicated SVG path(s), so the heatmap colour applies to the
 * actual muscle shape rather than a rectangle approximation.
 *
 * ## Why fetch-on-mount (not inline as a string constant)
 *
 * Each SVG is ~580KB unminified text (SVGO'd from ~2.3MB / 1.3MB
 * originals; gzips to ~150KB on the wire). Inlining both as JS
 * string literals would balloon the bundle by ~1.1MB even though
 * gzipped — and every dashboard page load would pay the parse cost
 * whether the user opened the Volume Map card or not. Fetching from
 * /public lets the bundler skip the SVG entirely and the browser
 * caches the file across visits.
 *
 * The fetch is fire-and-forget on mount; while in-flight we render
 * the existing geometric `<BodySilhouette>` as a placeholder. The
 * detailed SVG fades in once loaded.
 *
 * ## Painting model
 *
 * After dangerouslySetInnerHTML injects the SVG into a container div,
 * we useEffect → querySelectorAll("[data-muscle]") → for each <g>:
 *   1. Look up its data-muscle label in MUSCLE_LABEL_TO_REGION.
 *   2. Resolve a region (or null).
 *   3. Call fillFor(region) — caller's heatmap logic — and apply the
 *      result to the contained <path> (or recursively to descendants
 *      if the group nests).
 *
 * The "Outline" group (and any other null-region group) gets a fixed
 * neutral fill — the body silhouette colour — independent of the
 * heatmap. Otherwise the silhouette body would disappear into the
 * background, leaving just disembodied muscles.
 *
 * ## Recolouring + the fight with the source SVG
 *
 * The source SVGs ship with inline `fill:#e82e30` (red) on every
 * muscle path. The pre-svgo.mjs script strips those inline fills
 * during SVG generation so the React component's painted fill wins
 * cleanly. If the upstream SVG file ever gets regenerated and the
 * fill survives, the painted fill won't apply (browsers honour
 * inline > attribute) — fix by re-running the SVGO pipeline.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { BodyRegion } from "@/lib/volume-by-muscle";
import { regionForMuscleLabel } from "./muscle-label-map";

export type AnatomySide = "front" | "back";

export type AnatomySvgProps = {
  /** Which view to render. Front + back ship as separate SVG assets;
   *  the dashboard renders both side-by-side. */
  side: AnatomySide;
  /** Per-region fill resolver. Returns a CSS colour string (e.g.
   *  `"var(--color-brand)"`, `color-mix(...)`, or `"#ff7a1a"`). Called
   *  once per muscle <g> in the SVG; the result is applied to every
   *  <path> descendant of that group via `setAttribute("fill", ...)`.
   *
   *  When the muscle's label doesn't map to a region (neck muscles,
   *  the Outline group), `fillFor` is called with null and should
   *  return the neutral silhouette fill. */
  fillFor: (region: BodyRegion | null) => string;
  /** v9.253.4 — opt-in alignment group. AnatomySvg instances sharing
   *  the same `alignKey` coordinate via a module-level cache so their
   *  bodies render at the same vertical scale (necessary because each
   *  side's tight viewBox bounds differ — front and back have
   *  different arm spread, different head visibility, etc., so the
   *  per-side crops produced slightly different rendered sizes).
   *
   *  When this prop is set, the viewBox crop expands to the UNION of
   *  all sides in the group, so each body sits in the same canvas
   *  and renders at the same on-screen height. Without it, each
   *  side falls back to its own tight bounds (previous behaviour).
   *
   *  Use a stable string per surface — e.g. "dashboard-volumeMap".
   *  Different surfaces with their own alignment requirements get
   *  their own keys. */
  alignKey?: string;
  /** Optional className applied to the container div. */
  className?: string;
  /** Lever C — when set, muscle regions become tappable (delegated click)
   *  and get a pointer cursor; tapping a region calls this with its
   *  BodyRegion. Omit to keep the SVG purely decorative (default). */
  onRegionClick?: (region: BodyRegion) => void;
  /** Lever C — the currently-focused region. When set, every OTHER muscle
   *  dims (spotlight effect) so the tapped region stands out. The body
   *  outline (null region) never dims. */
  selectedRegion?: BodyRegion | null;
  /** When set, the focused region is outlined in this colour (a constant-width
   *  ring). The recovery map uses it because its red SELECTED_FILL collides with
   *  the overtrained fill, so the ring carries the selection while the muscle
   *  keeps its true state colour. Omit/null in modes that tint the fill. */
  selectedStroke?: string | null;
};

/** v9.270 — cache-busting version for the static anatomy SVG assets. BUMP this
 *  whenever public/anatomy/{front,back}.svg CONTENT changes (e.g. the deltoid
 *  front/rear data-muscle re-key) so browsers re-fetch the new file instead of
 *  serving the aggressively-cached old one (the fetch docblock relies on that
 *  caching for perf). Appended as ?v=N to the fetch URL; a stale cached SVG
 *  whose old data-muscle keys no longer resolve paints grey. */
const SVG_ASSET_VERSION = "2";

/** Module-level cache so the SVG is only fetched once per side across
 *  the whole app lifetime. Doubles as a fallback for users who navigate
 *  away + back to the dashboard — the second mount paints instantly. */
const svgCache = new Map<AnatomySide, string>();

/** Module-level cache of the tightened viewBox per side. Computed
 *  via browser getBBox() on first paint then reused — the bounds
 *  don't change across re-renders, only the fills do. */
const viewBoxCache = new Map<AnatomySide, string>();

/** Module-level cache of bbox per (alignKey, side) for the alignment
 *  coordination. Each AnatomySvg with an alignKey writes its own
 *  bbox here on first paint, computes a UNION across all sides in
 *  the group, and applies the union as the viewBox. Subscribers
 *  also recompute when the cache mutates so a late-mounting sibling
 *  triggers the earlier one to re-align.
 *
 *  Per-(alignKey, side) lets the same dashboard render multiple
 *  surfaces with independent alignment groups (one Volume Map +
 *  one future Strength Map, say, each with their own front/back
 *  pair). */
type Bbox = { x: number; y: number; width: number; height: number };
const alignBboxCache = new Map<string, Map<AnatomySide, Bbox>>();
const alignListeners = new Map<string, Set<() => void>>();

function notifyAlignListeners(alignKey: string) {
  const set = alignListeners.get(alignKey);
  if (!set) return;
  for (const fn of set) fn();
}

/**
 * v9.265.2 — aspect-matched per-side viewBox.
 *
 * History:
 *   - Original `unionViewBox` (v9.253.4): one viewBox covering the
 *     union of every side's bbox. Same canvas, but each body sat in
 *     its own bbox portion → side with content reaching further
 *     filled more of the canvas. Result: bodies looked different
 *     sizes.
 *   - `centeredViewBox` (v9.265): each side got the same canvas
 *     dimensions (maxW × maxH) with body centered. Canvases same
 *     size, but bodies filling different vertical proportions of
 *     their canvases — taller natural body still looked bigger.
 *     Beta feedback: "The REAR body view looks larger than the
 *     FRONT body... ensure the FRONT body is the same size and
 *     aligned."
 *   - `aspectMatchedViewBox` (v9.265.2, this one): each side's
 *     viewBox is TIGHT in height (own.height — body fills canvas
 *     vertically) and PADDED in width so all sides share the same
 *     aspect ratio. Result: every body fills its canvas vertically,
 *     every canvas has the same aspect ratio, so at equal column
 *     widths every body renders at the SAME HEIGHT.
 *
 * The math:
 *   target_aspect = min(own.height / own.width) across all sides
 *                 = the widest natural body's aspect
 *   For each side:
 *     target_width = own.height / target_aspect
 *     (≥ own.width by construction — only pads outward)
 *     xPad = (target_width - own.width) / 2  (symmetric horizontal)
 *
 *   Plus a small padding scaled to preserve aspect ratio:
 *     heightPad = pad
 *     widthPad  = pad / target_aspect
 *
 *   The final viewBox is centered on the body and has aspect
 *   exactly equal to target_aspect.
 *
 * At equal column widths, all sides render at the same SVG height.
 * Bodies fill their canvas vertically → rendered body heights match.
 * Narrower bodies appear with horizontal padding (centered); the
 * wider body fills the canvas edge-to-edge.
 */
function aspectMatchedViewBox(
  group: Map<AnatomySide, Bbox>,
  side: AnatomySide,
  pad: number,
): string | null {
  const own = group.get(side);
  // Bail on a degenerate bbox. A zero (or NaN) width would make the h/w
  // aspect Infinity/NaN and produce a 0-width or NaN viewBox string that
  // renders as a blank SVG. `> 0` rejects 0, negatives, and NaN in one check.
  // Returning null lets the caller fall back to the untightened default
  // viewBox (same graceful path as the getBBox try/catch). This only happens
  // if getBBox returns an empty box (element not laid out / display:none).
  if (!own || !(own.width > 0) || !(own.height > 0)) return null;
  // Find the WIDEST natural aspect (smallest h/w) across sides.
  // Each side will pad WIDTH to match this aspect; the wider
  // side's body fills its canvas edge-to-edge, narrower sides
  // get symmetric horizontal padding. Bodies always fill canvas
  // HEIGHT (own.height), guaranteeing same rendered height
  // when canvases are sized equally.
  let minAspect = own.height / own.width;
  for (const b of group.values()) {
    if (!(b.width > 0) || !(b.height > 0)) continue; // skip degenerate sides
    const a = b.height / b.width;
    if (a < minAspect) minAspect = a;
  }
  if (!(minAspect > 0) || !Number.isFinite(minAspect)) return null;
  const targetWidth = own.height / minAspect;
  const xPad = (targetWidth - own.width) / 2;
  // Scale the pad so aspect stays EXACTLY minAspect (otherwise the
  // small viewBox boundary drift would re-introduce the alignment
  // issue we're solving). heightPad in source units; widthPad
  // computed so widthPad/heightPad = 1/minAspect (matches the
  // viewBox aspect for the unpadded body).
  const heightPad = pad;
  const widthPad = pad / minAspect;
  return `${own.x - xPad - widthPad} ${own.y - heightPad} ${targetWidth + widthPad * 2} ${own.height + heightPad * 2}`;
}

export function AnatomySvg({
  side,
  fillFor,
  alignKey,
  className,
  onRegionClick,
  selectedRegion,
  selectedStroke,
}: AnatomySvgProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [svg, setSvg] = useState<string | null>(
    () => svgCache.get(side) ?? null,
  );

  // Fetch the SVG once per side. If cached, the useState initialiser
  // already populated svg synchronously — no fetch needed. Otherwise
  // kick off the network round-trip; cache the result for subsequent
  // mounts.
  //
  // No abort controller: the fetched SVG is a static asset (no
  // sensitive data), and dropping the result if the component
  // unmounted mid-fetch is OK — the setState bail-out below handles
  // it. Keeping the code simpler over technically-correct cleanup.
  useEffect(() => {
    if (svg != null) return;
    let cancelled = false;
    fetch(`/anatomy/${side}.svg?v=${SVG_ASSET_VERSION}`)
      .then((r) => r.text())
      .then((text) => {
        // Cache BEFORE the cancellation check. If this instance unmounted
        // mid-download (e.g. a parent re-render shifted siblings and forced
        // a remount), the in-flight fetch is cancelled — but the asset is
        // still valid, so caching it means the NEXT mount's useState
        // initialiser (svgCache.get) gets it instantly instead of starting
        // yet another ~0.5MB fetch. Repeated remounts during the download
        // were otherwise cancelling every fetch in turn, leaving the body
        // permanently un-injected (and rendering as the raw black SVG).
        svgCache.set(side, text);
        if (cancelled) return;
        setSvg(text);
      })
      .catch((err) => {
        // Network failure or 404 — log + degrade silently. The
        // container stays empty; caller's fallback chrome (caption,
        // legend) still renders.
        console.error(`[AnatomySvg] failed to load ${side}.svg:`, err);
      });
    return () => {
      cancelled = true;
    };
  }, [side, svg]);

  // Paint pass — walks the rendered DOM after each fill change.
  // Reads every [data-muscle] group, resolves its region, calls
  // fillFor, then applies the result to each <path> descendant.
  //
  // We re-run this whenever `fillFor` changes (it's a fresh function
  // identity each parent render, but the parent should `useMemo` it
  // if it wants to avoid redundant repaints). The DOM walk itself is
  // cheap (~25–50 nodes per SVG) so even a per-render repaint is
  // fine in practice.
  //
  // v9.253.3 — also crop the viewBox to the tight content bounds on
  // first paint per side. The source SVG ships with a 3000×3000
  // viewBox but the actual body silhouette occupies maybe 1500×2600
  // of that — empty space on either side was making the rendered
  // anatomies look ~50% smaller than the card cell they sat in.
  // getBBox() on the root <g> returns the visible bounds; we set
  // the resulting viewBox once + cache the string so subsequent
  // mounts (and the fill-only re-runs of this effect) skip the
  // recompute. Padding of 20 viewBox units keeps the body off the
  // exact edge.
  // Paint pass extracted into a stable callback so it can run from BOTH
  // the svg/fill effect AND a MutationObserver (below). The observer
  // re-applies fills whenever React (re)injects the SVG child — covering
  // late injection and any re-commit that would otherwise leave the raw
  // (black) asset un-painted. Safe to call before injection: querySelector
  // returns null / the group list is empty, so it's a no-op until the SVG
  // is in the DOM.
  const applyPaint = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;

    // Tighten the viewBox once per side. Browser's getBBox returns
    // the rendered extent of the element (including all descendants)
    // in the element's own coordinate space.
    //
    // v9.253.4 — when `alignKey` is set, write own bbox into the
    // shared cache + use the UNION across all sides in the group.
    // This makes front + back render at the same vertical scale +
    // baseline (previously each had its own tight crop so they
    // floated at different y positions).
    const svgEl = root.querySelector<SVGSVGElement>("svg");
    if (svgEl) {
      if (alignKey) {
        // Shared-alignment path. Compute own bbox first time + cache
        // it under the group; then assemble the union viewBox from
        // all cached sides under this group.
        try {
          let group = alignBboxCache.get(alignKey);
          if (!group) {
            group = new Map();
            alignBboxCache.set(alignKey, group);
          }
          if (!group.has(side)) {
            const rootG = svgEl.querySelector<SVGGElement>("g");
            if (rootG) {
              const bbox = rootG.getBBox();
              group.set(side, bbox);
              // Notify other instances in the same group that the
              // cache grew — they re-read + re-apply the union so
              // a late-mounting sibling triggers earlier ones to
              // expand their viewBox to the new shared bounds.
              notifyAlignListeners(alignKey);
            }
          }
          if (group.size > 0) {
            const vb = aspectMatchedViewBox(group, side, 20);
            if (vb) svgEl.setAttribute("viewBox", vb);
          }
        } catch (err) {
          console.error(`[AnatomySvg] getBBox failed for ${side}:`, err);
        }
      } else {
        // Standalone path — tight crop per side, no coordination.
        const cached = viewBoxCache.get(side);
        if (cached) {
          svgEl.setAttribute("viewBox", cached);
        } else {
          // getBBox throws if the SVG isn't laid out yet — guard so
          // a racey first paint doesn't crash. In practice the
          // element is laid out by the time useEffect fires
          // post-paint, but belt-and-braces.
          try {
            const rootG = svgEl.querySelector<SVGGElement>("g");
            if (rootG) {
              const bbox = rootG.getBBox();
              const pad = 20;
              const tight = `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`;
              svgEl.setAttribute("viewBox", tight);
              viewBoxCache.set(side, tight);
            }
          } catch (err) {
            // getBBox failure is non-fatal — the SVG still renders
            // at its original viewBox, just with more empty space.
            console.error(`[AnatomySvg] getBBox failed for ${side}:`, err);
          }
        }
      }
    }

    const groups = Array.from(
      root.querySelectorAll<SVGGElement>("[data-muscle]"),
    );
    // Baseline (untrained / uncovered) fill per the caller's resolver. Lets
    // us spot un-worked muscle groups so they can fade back when nothing is
    // selected — WITHOUT coupling this component to the actual colour value
    // (the caller owns the palette; we just compare against its baseline).
    const baselineFill = fillFor(null);
    // Pre-resolve each group's region + fill once, so fillFor runs once per
    // group.
    const painted = groups.map((g) => {
      const region = regionForMuscleLabel(g.getAttribute("data-muscle") ?? "");
      return { g, region, fill: fillFor(region) };
    });
    for (const { g, region, fill } of painted) {
      // Apply to the group AND its <path> descendants — some SVGs
      // have the fill on the group, others on individual paths.
      // Setting both is harmless and covers both shapes. Fill only:
      // we deliberately don't stroke (a v9.272 outline/seam-closer
      // experiment was dropped — the raw fill reads cleaner; see git
      // history if a muscle outline is ever wanted again).
      g.setAttribute("fill", fill);
      // Opacity has two cases; the body outline (null region) never fades, so
      // the silhouette always holds. Empty string clears it (full strength).
      //   - Lever C spotlight: a region is selected → dim every OTHER muscle
      //     hard (0.3) so the focused one pops.
      //   - Idle fade: nothing selected → fade the UNTRAINED muscles (those
      //     resolving to the baseline fill) to 0.3 so the worked (orange)
      //     muscles carry the figure. This ALSO applies when nothing is worked
      //     (empty window / new user): every muscle recedes to the faded
      //     "nothing logged yet" look — matching PLAN's uncovered state — while
      //     the solid outline keeps the body readable. (An earlier hasWorked
      //     guard kept the empty state solid grey; that read as a bug next to
      //     PLAN's faded gaps, so it was dropped.)
      // NOTE: the loose `!= null` is load-bearing. Empty-state callers omit
      // selectedRegion (it's undefined), and undefined must read as "no
      // selection" so we take the idle-fade branch — a strict `!== null` (or
      // a `= null` default) would send undefined into the spotlight branch
      // and dim EVERY region. Keep it loose.
      let opacity = "";
      if (selectedRegion != null) {
        if (region != null && region !== selectedRegion) opacity = "0.3";
      } else if (region != null && fill === baselineFill) {
        opacity = "0.3";
      }
      g.style.opacity = opacity;
      // Selection ring — outline the focused muscle so it reads as selected
      // regardless of its fill (the recovery map can't tint it red, which is the
      // overtrained colour). vector-effect keeps the ring a constant ~2px at any
      // SVG scale; it's cleared on every other muscle so only the focused one is
      // outlined.
      const ring =
        selectedStroke && region != null && region === selectedRegion
          ? selectedStroke
          : "";
      const paths = g.querySelectorAll("path, rect, polygon, ellipse, circle");
      for (const p of Array.from(paths)) {
        const el = p as SVGElement;
        el.setAttribute("fill", fill);
        el.style.stroke = ring;
        el.style.strokeWidth = ring ? "2" : "";
        if (ring) el.setAttribute("vector-effect", "non-scaling-stroke");
        else el.removeAttribute("vector-effect");
      }
    }
  }, [fillFor, side, alignKey, selectedRegion, selectedStroke]);

  // Run the paint when the SVG injects or the fill rules change.
  useEffect(() => {
    if (!svg) return;
    applyPaint();
  }, [svg, applyPaint]);

  // Re-apply paint whenever the container's direct children change — i.e.
  // when React (re)injects the <svg> via dangerouslySetInnerHTML. This is
  // what makes the fills resilient to a re-commit/remount that would
  // otherwise reset the asset to its raw black paths with no repaint.
  // `childList` only (NOT subtree) so our own fill-setting on descendant
  // paths doesn't retrigger the observer (no infinite loop).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new MutationObserver(() => applyPaint());
    obs.observe(root, { childList: true });
    return () => obs.disconnect();
  }, [applyPaint]);

  // Lever C — delegated click handler. One listener on the container
  // resolves the tapped [data-muscle] group to a BodyRegion and fires
  // onRegionClick. Delegation (vs per-node listeners) avoids re-binding on
  // every fill repaint. Only active when onRegionClick is provided.
  useEffect(() => {
    if (!svg || !onRegionClick) return;
    const root = containerRef.current;
    if (!root) return;
    const handler = (e: Event) => {
      const target = e.target as Element | null;
      const g = target?.closest("[data-muscle]");
      if (!g) return;
      const region = regionForMuscleLabel(g.getAttribute("data-muscle") ?? "");
      if (region) onRegionClick(region);
    };
    root.addEventListener("click", handler);
    return () => root.removeEventListener("click", handler);
  }, [svg, onRegionClick]);

  // v9.253.4 — subscribe to align-cache mutations from sibling
  // AnatomySvg instances in the same group. When a sibling mounts
  // later and writes its bbox, this listener fires + we re-apply
  // the (now larger) union viewBox. Without this, the FIRST side
  // to mount would set its viewBox using only its own bounds and
  // not pick up the SECOND side's bounds when they arrive.
  //
  // Subscription is only meaningful when alignKey is set; standalone
  // mode has nothing to coordinate.
  useEffect(() => {
    if (!alignKey || !svg) return;
    const recompute = () => {
      const root = containerRef.current;
      if (!root) return;
      const svgEl = root.querySelector<SVGSVGElement>("svg");
      if (!svgEl) return;
      const group = alignBboxCache.get(alignKey);
      if (!group || group.size === 0) return;
      const vb = aspectMatchedViewBox(group, side, 20);
      if (vb) svgEl.setAttribute("viewBox", vb);
    };
    let set = alignListeners.get(alignKey);
    if (!set) {
      set = new Set();
      alignListeners.set(alignKey, set);
    }
    set.add(recompute);
    return () => {
      set?.delete(recompute);
    };
  }, [alignKey, svg, side]);

  return (
    <div
      ref={containerRef}
      className={
        // w-full h-auto so the SVG scales to its container; the
        // viewBox preserves aspect ratio. aria-hidden because the
        // heatmap is decorative — the data lives in the captions
        // beneath the card. When interactive (Lever C), muscle groups
        // get a pointer cursor to signal they're tappable.
        "w-full [&_svg]:w-full [&_svg]:h-auto " +
        (onRegionClick ? "[&_[data-muscle]]:cursor-pointer " : "") +
        (className ?? "")
      }
      aria-hidden
      // dangerouslySetInnerHTML is the right tool for a static SVG
      // asset that we control end-to-end (we generated the file
      // ourselves via SVGO + pre-process). No user-supplied content
      // ever lands here.
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
}
